import React, { useEffect, useMemo, useState } from "react";
import { Box, Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFetchTable } from "@hooks/useFetchDataTable";
import BackgroundImage from "@components/BackgroundImageCst";
import Table from "@components/Table";
import HeroCst from "@components/HeroCst";
import Breadcrumb from "@components/Breadcrumb";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalDownloadUploadCst from "@components/ModalDownloadUploadCst";
import { ActionUploadDownloadIterface } from "@components/ModalDownloadUploadCst/types";
import { ActionsType } from "src/interfaces/Common";
import { MethodType } from "src/services/type";
import { fetcher } from "src/services/config";
import useSWRMutation from "swr/mutation";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { useParams } from "react-router-dom";
import CustomRowAction from "@components/Table/components/CustomRowAction";
import ButtonBackPage from "@components/ButtonBackPage";
import { useFetchDetail } from "@hooks/useFetchDetail";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";

const GoodsReceiptsCost: React.FC = () => {
  const navigate = useNavigate();
  const { idCost, idProject, id } = useParams();
  const [idReceiptDeleted, setidReceiptDeleted] = useState<string>();
  const [openUpload, setOpenUpload] = useState<ActionUploadDownloadIterface>();
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${PagesID["projects.cost-goods-receipts"]}`;

  const { columns, dataTable, actionsTopToolbar, actionForRow, mutate } =
    useFetchTable(
      `${ServicesURL.getGoodsReceipts.replace(":costId", `${idCost}`)}`,
      PagesID["projects.cost-goods-receipts"],
      filters[filterId],
    );

  const { dataDetail: dataCost } = useFetchDetail(
    `${ServicesURL.getProjectsCost}`,
    idCost,
  );

  const { t } = useTranslation(["goods-receipts-page"]);
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
  }>();

  const { trigger: triggerDeleteReceipt } = useSWRMutation(
    `${ServicesURL.getGoodsReceipts.replace(
      ":costId",
      `${idCost}`,
    )}/${idReceiptDeleted}`,
    (url: string) => {
      return fetcher<any>(url, MethodType.DELETE);
    },
  );

  const handleDelete = React.useCallback(async () => {
    if (idReceiptDeleted) {
      setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
      try {
        await triggerDeleteReceipt();
        const newData = [
          ...dataTable.output.filter((el: any) => el.id !== idReceiptDeleted),
        ];
        mutate && mutate({ ...dataTable, ...{ output: newData } });
        setOpenToast({ s: StatusEnum.OK, message: "" });
      } catch (error) {
        setidReceiptDeleted(undefined);
        setOpenToast(undefined);
        console.error(error);
      }
    }
  }, [dataTable, idReceiptDeleted, mutate, triggerDeleteReceipt]);

  useEffect(() => {
    if (!idReceiptDeleted) return;
  }, [idReceiptDeleted]);

  useEffect(() => {
    if (
      dataTable?.output &&
      Array.isArray(dataTable.output) &&
      dataCost &&
      dataCost.total_cost &&
      typeof dataCost?.total_cost === "number"
    ) {
      const totalOutput = dataTable.output
        .filter((item: any) => item.status !== "REJECTED")
        .reduce((sum: number, item: any) => sum + item.value, 0);

      setShowAdd(totalOutput < dataCost.total_cost);
    } else {
      setShowAdd(false);
    }
  }, [dataTable, dataCost, mutate]);

  const filteredColumns = useMemo(() => {
    if (columns) {
      return columns.map((el) => {
        return { ...el };
      });
    }
  }, [columns]);

  const configurationActionForRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => {
      switch (action.type) {
        case "delete": {
          return {
            ...action,
            callBack: (id: string) => {
              setidReceiptDeleted(id);
              setOpenModalDelete(true);
            },
          };
        }
        default:
          return action;
      }
    });
  }, [actionForRow]);

  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = configurationActionForRow && [
      ...configurationActionForRow,
    ];

    //FUNZIONE PER ABILITAZIONE/ DISABILITAZIONE CAMPI IN BASE A VALORI DELLA ROW
    configRowNew = configRowNew.map((el: ActionsType) => {
      if (
        (el.type === "delete" || el.type === "edit") &&
        !row.original?.deletable
      ) {
        return { ...el, disabled: true };
      }

      return { ...el };
    });

    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={configRowNew}
      />,
    ];
  };

  const actionsToolbar: ActionsType[] | undefined = React.useMemo(() => {
    return actionsTopToolbar?.map((action: ActionsType) => {
      switch (action.type) {
        case "add":
          return {
            ...action,
            ...{ disabled: !showAdd },
          };
        default:
          return action;
      }
    });
  }, [actionsTopToolbar, showAdd]);

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-cost-centers.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={`${t("hero-title")}: ${dataCost?.title || ""}`}
          description={`${t("total-costs")}: ${new Intl.NumberFormat("it-IT", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(Number(dataCost?.total_cost || 0))} €`}
        />
        <ButtonBackPage
          customNavigate={() =>
            navigate(
              `/corporate/${id}/detail/budget-management/projects/edit/${idProject}`,
              { state: { tab: "costs" } },
            )
          }
        />
        <Box
          sx={{
            mt: "25px",
          }}
        >
          <Table
            id={PagesID["projects.cost-goods-receipts"]}
            data={dataTable?.output || []}
            columns={filteredColumns || []}
            configurationTopToolbar={actionsToolbar}
            renderRowActions={customRenderRowActions}
            totalPages={dataTable?.metadata?.total_elements}
            modalTitle={t("hero-title")?.replace("-", "") || ""}
          />
        </Box>
      </Container>

      <ModalConfirmsCst
        open={!!openModalDelete}
        title={t("delete-item", { ns: "modal" })}
        description={t("confirm-delete", { ns: "modal" })}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setOpenModalDelete(!openModalDelete);
          setOpenToast(undefined);
        }}
        onActionConfirmed={() => handleDelete()}
        status={openToast}
      />
      <ModalDownloadUploadCst
        open={!!openUpload}
        setOpen={() => {
          setOpenUpload(undefined);
        }}
        title={openUpload?.labelTranslate || ""}
        data={openUpload}
      />
    </AnimationFadeIn>
  );
};

export default GoodsReceiptsCost;
